/**
 * Microphone recorded beside the live H.264 picture.
 * Chrome and Safari both use this. The video file stays silent until the
 * server copies the picture and muxes this audio in.
 */

const AUDIO_MIME_CANDIDATES = Object.freeze([
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
]);

export function pickSidecarAudioMime(isTypeSupported) {
  if (typeof isTypeSupported !== "function") return "";
  for (const type of AUDIO_MIME_CANDIDATES) {
    try {
      if (isTypeSupported(type)) return type;
    } catch {
      /* try the next container */
    }
  }
  return "";
}

function recorderMimeProbe() {
  const Recorder = typeof MediaRecorder !== "undefined" ? MediaRecorder : null;
  if (!Recorder || typeof Recorder.isTypeSupported !== "function") return null;
  return (type) => Recorder.isTypeSupported(type);
}

function stopOwnedTracks(stream, originalTrack) {
  stream?.getTracks?.().forEach((track) => {
    if (track === originalTrack) return;
    try {
      track.stop();
    } catch {
      /* ignore */
    }
  });
}

/**
 * @param {MediaStreamTrack|null|undefined} track
 * @returns {null|{ rotate: () => Promise<Blob|null>, stop: () => Promise<Blob|null> }}
 */
export function createSidecarAudioRecorder(track) {
  if (!track || typeof MediaRecorder === "undefined") return null;
  let stream;
  try {
    const cloned = typeof track.clone === "function" ? track.clone() : track;
    stream = new MediaStream([cloned]);
  } catch {
    return null;
  }

  const mime = pickSidecarAudioMime(recorderMimeProbe());
  let recorder = null;
  let chunks = [];

  const openRecorder = () => {
    const opts = { audioBitsPerSecond: 64_000 };
    if (mime) opts.mimeType = mime;
    chunks = [];
    try {
      recorder = new MediaRecorder(stream, opts);
    } catch {
      try {
        recorder = new MediaRecorder(stream);
      } catch {
        recorder = null;
        return false;
      }
    }
    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunks.push(event.data);
    };
    try {
      recorder.start();
      return true;
    } catch {
      recorder = null;
      return false;
    }
  };

  if (!openRecorder()) {
    stopOwnedTracks(stream, track);
    return null;
  }

  const takeBlob = () =>
    new Promise((resolve) => {
      const rec = recorder;
      if (!rec) {
        resolve(null);
        return;
      }
      const finish = () => {
        const type = rec.mimeType || mime || "audio/mp4";
        const parts = chunks;
        chunks = [];
        resolve(parts.length ? new Blob(parts, { type }) : null);
      };
      rec.onstop = finish;
      try {
        if (rec.state === "recording") rec.stop();
        else finish();
      } catch {
        finish();
      }
    });

  return {
    async rotate() {
      const blob = await takeBlob();
      openRecorder();
      return blob;
    },
    async stop() {
      const blob = await takeBlob();
      recorder = null;
      stopOwnedTracks(stream, track);
      return blob;
    },
  };
}
